import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { DateRange } from "react-date-range";
import ReactJWPlayer from "react-jw-player";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Badge, EmptyState } from "../ui";
import { getAnalytics } from "../../services/analytics";
import { getDate } from "../../utils/format";
import profileImage from "../../assets/images/user/profile-default.png";
import "./detail.css";

/**
 * Loads a content item into the redux store on mount and clears it when the
 * user leaves `pagePath`. Returns the stored item (first element) and whether
 * it is still loading.
 */
export function useStoredDetail({ load, setInfo, storeKey, pagePath }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const stored = useSelector((state) => state.uploadMediaReducer[storeKey]);

  useEffect(() => {
    load().then((data) => {
      dispatch(setInfo(data));
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () =>
      history.listen((location) => {
        if (location.pathname !== pagePath) dispatch(setInfo([]));
      }),
    [history, pagePath, dispatch, setInfo]
  );

  return { item: (stored ?? [])[0], loading };
}

/**
 * Dates sent to the analytics API: the picked range, defaulting to the
 * content creation date and today.
 */
export function analyticsRange(range, createdAt, today = new Date()) {
  return {
    start_date: getDate(range.startDate || createdAt),
    end_date: range.endDate
      ? getDate(range.endDate)
      : today.toISOString().split("T")[0],
  };
}

/** JW Player analytics for `mediaIds`, refetched when the date range changes. */
export function useMediaAnalytics(mediaIds, createdAt, enabled) {
  const [range, setRange] = useState({ startDate: "", endDate: "" });
  const [analysis, setAnalysis] = useState({ metrics: [], rows: [] });
  const ids = (mediaIds || []).filter(Boolean).join(",");

  useEffect(() => {
    if (!enabled || !ids) return undefined;
    let cancelled = false;
    getAnalytics({
      media_ids: ids.split(","),
      ...analyticsRange(range, createdAt),
    })
      .then((response) => {
        if (cancelled) return;
        setAnalysis({
          metrics: response.metadata?.column_headers?.metrics || [],
          rows: response.data?.rows || [],
        });
      })
      .catch((error) => console.error(error));
    return () => {
      cancelled = true;
    };
  }, [ids, createdAt, enabled, range]);

  return { ...analysis, range, setRange };
}

const minutes = (value) => `${(value / 60).toFixed(2)} minutes`;

// Order of the metrics returned by the analytics API (column 0 is the media id).
const METRICS = [
  { icon: "ri-play-circle-line", color: "blue", format: (v) => v },
  { icon: "ri-time-line", color: "amber", format: minutes },
  { icon: "ri-star-line", color: "green", format: (v) => v },
  { icon: "ri-checkbox-circle-line", color: "violet", format: (v) => v },
  {
    icon: "ri-percent-line",
    color: "amber",
    format: (v) => `${parseFloat(v).toFixed(2)} %`,
  },
  {
    icon: "ri-eye-line",
    color: "blue",
    format: (v) => parseFloat(v).toFixed(2),
  },
  { icon: "ri-timer-line", color: "green", format: minutes },
];

/** Title row of a detail page: back button, title, badges and actions. */
export const DetailHeader = ({ title, badges, actions }) => {
  const history = useHistory();
  const { t } = useTranslation();
  return (
    <header className="detail-header">
      <button
        type="button"
        className="ui-icon-btn"
        onClick={() => history.goBack()}
        title={t("Back")}
        aria-label={t("Back")}
      >
        <i className="ri-arrow-left-line" aria-hidden="true"></i>
      </button>
      <div className="detail-header-body">
        <h1 className="ui-page-title">{title}</h1>
        {badges && <div className="detail-badges">{badges}</div>}
      </div>
      {actions && <div className="ui-page-actions">{actions}</div>}
    </header>
  );
};

/** JW Player for `mediaId`, or `emptyText` when nothing was uploaded. */
export const MediaPlayer = ({ mediaId, emptyText }) => (
  <div className="detail-player">
    {mediaId ? (
      <ReactJWPlayer
        playerId="4t00MwmP"
        playerScript="https://content.jwplatform.com/libraries/4t00MwmP.js"
        controls={true}
        playlist={`https://cdn.jwplayer.com/v2/media/${mediaId}`}
        isAutoPlay={false}
      />
    ) : (
      <EmptyState icon="ri-film-line">{emptyText}</EmptyState>
    )}
  </div>
);

/**
 * Label / value pairs. `items`: [{ label, value }]. Values are capitalized
 * unless `plain` (emails, names…).
 */
export const FactList = ({ items, plain }) => (
  <dl className="detail-facts" data-plain={plain ? "" : undefined}>
    {items.map(({ label, value }) => (
      <div key={label} className="detail-fact">
        <dt>{label}</dt>
        <dd>{value === undefined || value === "" ? "—" : value}</dd>
      </div>
    ))}
  </dl>
);

/** Titled list of read-only chips (genres, tags…). */
export const TagList = ({ label, items }) => (
  <div className="detail-section">
    <h3 className="detail-section-title">{label}</h3>
    {items && items.length > 0 ? (
      <div className="detail-tags">
        {items.map((item, index) => (
          <span key={index} className="detail-tag">
            {item}
          </span>
        ))}
      </div>
    ) : (
      <p className="detail-muted">—</p>
    )}
  </div>
);

/** Tab bar. `tabs`: [{ id, label, icon }]. */
export const DetailTabs = ({ tabs, value, onChange }) => (
  <div className="detail-tabs" role="tablist">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        type="button"
        role="tab"
        aria-selected={value === tab.id}
        onClick={() => onChange(tab.id)}
      >
        <i className={tab.icon} aria-hidden="true"></i>
        {tab.label}
      </button>
    ))}
  </div>
);

/** Date range button + calendar popover. `emptyLabel` is shown without range. */
export const RangePicker = ({ range, onChange, emptyLabel }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const hasRange = range.startDate && range.endDate;
  return (
    <div className="detail-range" ref={ref}>
      <button
        type="button"
        className="detail-range-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <i className="ri-calendar-line" aria-hidden="true"></i>
        {hasRange
          ? `${getDate(range.startDate)} → ${getDate(range.endDate)}`
          : emptyLabel || t("Since publication")}
      </button>
      {hasRange && (
        <button
          type="button"
          className="ui-icon-btn"
          title={t("Clear")}
          aria-label={t("Clear")}
          onClick={() => onChange({ startDate: "", endDate: "" })}
        >
          <i className="ri-close-line" aria-hidden="true"></i>
        </button>
      )}
      {open && (
        <div className="detail-range-popover">
          <DateRange
            editableDateInputs={true}
            onChange={(item) =>
              onChange({
                startDate: item.selection.startDate,
                endDate: item.selection.endDate,
              })
            }
            moveRangeOnFirstSelection={false}
            ranges={[
              {
                startDate: range.startDate || new Date(),
                endDate: range.endDate || new Date(),
                key: "selection",
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

/** Sum of each analytics column over all rows (one row per media). */
export const sumColumns = (rows) =>
  rows.reduce(
    (totals, row) =>
      row.map((value, i) =>
        i === 0 ? value : (totals[i] || 0) + Number(value)
      ),
    []
  );

/**
 * Analytics cards with a date range picker. Values are summed over all rows;
 * `filters` are extra controls shown next to the date picker.
 */
export const AnalyticsPanel = ({
  metrics,
  rows,
  range,
  setRange,
  emptyText,
  filters,
}) => {
  if (!metrics || metrics.length === 0) {
    return <EmptyState icon="ri-bar-chart-2-line">{emptyText}</EmptyState>;
  }
  const row = rows && rows.length > 0 ? sumColumns(rows) : null;
  return (
    <div className="detail-analytics">
      <div className="detail-analytics-toolbar">
        {filters}
        <RangePicker range={range} onChange={setRange} />
      </div>
      <div className="detail-metrics">
        {METRICS.map((metric, index) => (
          <div key={index} className="detail-metric">
            <span
              className="detail-metric-icon"
              style={{ "--metric-color": `var(--ui-${metric.color})` }}
              aria-hidden="true"
            >
              <i className={metric.icon}></i>
            </span>
            <div style={{ minWidth: 0 }}>
              <p className="detail-metric-label">{metrics[index]?.display}</p>
              <p className="detail-metric-value">
                {row ? metric.format(row[index + 1]) : 0}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Users' comments, newest as returned by the API. */
export const CommentList = ({ comments }) => {
  const { t } = useTranslation();
  if (!comments || comments.length === 0) {
    return (
      <EmptyState icon="ri-chat-3-line">
        {t("No comments have been added yet!")}
      </EmptyState>
    );
  }
  return (
    <ul className="detail-comments">
      {comments.map((comment) => (
        <li key={comment?._id} className="detail-comment">
          <img
            src={comment?.user?.profile_image?.url || profileImage}
            alt=""
            width="40"
            height="40"
          />
          <div style={{ minWidth: 0 }}>
            <div className="detail-comment-meta">
              <strong>{comment?.user?.name}</strong>
              <span>{getDate(comment.createdAt)}</span>
            </div>
            <p>{comment.comment}</p>
          </div>
        </li>
      ))}
    </ul>
  );
};

/** Crew members with their role. */
export const CrewList = ({ members }) => {
  const { t } = useTranslation();
  if (!members || members.length === 0) {
    return <EmptyState icon="ri-group-line">{t("No Data Found")}</EmptyState>;
  }
  return (
    <ul className="detail-crew">
      {members.map((member, index) => (
        <li key={member?._id || index}>
          <span className="detail-crew-avatar" aria-hidden="true">
            {(member?.member_name || "?").charAt(0).toUpperCase()}
          </span>
          <div style={{ minWidth: 0 }}>
            <strong>{member?.member_name}</strong>
            <span>{member?.member_role}</span>
          </div>
        </li>
      ))}
    </ul>
  );
};

/** Placeholder while a detail page loads. */
export const DetailSkeleton = () => (
  <div>
    <div
      className="ui-skeleton"
      style={{ width: "40%", height: 32, marginBottom: 24 }}
    />
    <div className="detail-layout">
      <div className="ui-skeleton" style={{ aspectRatio: "16 / 9" }} />
      <div className="ui-skeleton" style={{ minHeight: 280 }} />
    </div>
  </div>
);

/** Yes/no flag rendered as a badge (monetization…). */
export const OnOffBadge = ({ on }) => {
  const { t } = useTranslation();
  return on ? (
    <Badge color="green">{t("On")}</Badge>
  ) : (
    <Badge>{t("Off")}</Badge>
  );
};
